import { useState, useEffect } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { BOOKING_URL } from '../../data/site'
import ResponsiveImage from '../common/ResponsiveImage'

const navLinks = [
  { name: 'Home', to: '/' },
  { name: 'About', to: '/about' },
  { name: 'Services', to: '/services' },
  { name: 'Conditions', to: '/conditions' },
  { name: 'Contact', to: '/contact' },
]

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const location = useLocation()

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : 'unset'
    return () => { document.body.style.overflow = 'unset' }
  }, [isMenuOpen])

  useEffect(() => {
    setIsMenuOpen(false)
  }, [location.pathname])

  return (
    <>
      {/* Ribbon menu button — fixed to top-right, hangs like a bookmark */}
      <div
        className="fixed top-0 right-6 sm:right-8 lg:right-10 z-[60]"
        style={{
          filter: 'drop-shadow(0 0 1px rgba(197,72,25,0.5)) drop-shadow(0 3px 6px rgba(0,0,0,0.12))',
        }}
      >
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="flex flex-col items-center w-[52px] pt-3 pb-6 bg-[#f0e0d0] transition-colors duration-300"
          style={{
            clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 14px), 50% 100%, 0 calc(100% - 14px))',
          }}
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMenuOpen}
        >
        <span className="font-script text-[11px] text-dark-500 italic tracking-wide mb-2">
          {isMenuOpen ? 'Close' : 'Menu'}
        </span>

        {/* Hamburger / X */}
        <div className="relative w-5 h-[14px]">
          <span
            className="absolute left-0 right-0 h-[1.5px] rounded-full bg-dark-600 transition-all duration-300"
            style={{
              top: isMenuOpen ? '6px' : '0px',
              transform: isMenuOpen ? 'rotate(45deg)' : 'none',
            }}
          />
          <span
            className="absolute left-0 h-[1.5px] rounded-full bg-dark-600 transition-all duration-300"
            style={{
              top: '6px',
              opacity: isMenuOpen ? 0 : 1,
              width: isMenuOpen ? '20px' : '14px',
            }}
          />
          <span
            className="absolute left-0 h-[1.5px] rounded-full bg-dark-600 transition-all duration-300"
            style={{
              top: isMenuOpen ? '6px' : '12px',
              width: isMenuOpen ? '20px' : '17px',
              transform: isMenuOpen ? 'rotate(-45deg)' : 'none',
            }}
          />
        </div>
      </button>
      </div>

      {/* Full-screen menu overlay — two panels */}
      <AnimatePresence>
        {isMenuOpen && (
          <div className="fixed inset-0 z-[55]" onClick={() => setIsMenuOpen(false)}>
            {/* Left panel */}
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'tween', duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="absolute top-0 left-0 bottom-0 w-full md:w-1/2 bg-cream-100 flex flex-col justify-center overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <nav className="px-10 sm:px-14 md:px-16 lg:px-20">
                <ul className="space-y-1">
                  {navLinks.map((link) => (
                    <li key={link.name}>
                      <NavLink
                        to={link.to}
                        onClick={() => setIsMenuOpen(false)}
                        className={({ isActive }) =>
                          `block py-3 font-display text-4xl sm:text-5xl md:text-5xl lg:text-6xl transition-colors duration-200 ${
                            isActive
                              ? 'text-brand-orange-500'
                              : 'text-dark-700 hover:text-brand-orange-500'
                          }`
                        }
                      >
                        {link.name}
                      </NavLink>
                    </li>
                  ))}

                  <li>
                    <a
                      href={BOOKING_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setIsMenuOpen(false)}
                      className="block py-3 font-display text-4xl sm:text-5xl md:text-5xl lg:text-6xl text-brand-green-600 hover:text-brand-green-500 transition-colors duration-200"
                    >
                      Book
                    </a>
                  </li>
                </ul>

                <motion.p
                  className="mt-10 font-script text-brand-orange-400 italic text-sm md:text-base"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.7, duration: 0.6 }}
                >
                  confidence in motion
                </motion.p>
              </nav>
            </motion.div>

            {/* Right panel — hidden on mobile */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: 0.08 }}
              className="absolute top-0 right-0 bottom-0 w-1/2 hidden md:block overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <ResponsiveImage
                src="/nav-menu.jpg"
                alt="Recreo Rehab treatment room with natural light overlooking Midtown Toronto"
                sizes="50vw"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-brand-orange-500/15 mix-blend-multiply" />

              <motion.div
                className="absolute bottom-12 left-10 right-10"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.6 }}
              >
                <p className="font-display text-white text-2xl lg:text-3xl leading-snug drop-shadow-lg">
                  Midtown Toronto&apos;s path<br />
                  to <em className="italic">pain-free</em> movement.
                </p>
              </motion.div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  )
}

export default Header
